import { Rocket, Settings, User } from "lucide-react";
import type { LucideIcon } from "lucide-react";

export interface DocsArticle {
  slug: string;
  filename: string;
  category: string;
  titleKey: string;
  descriptionKey: string;
  order: number;
  // When set, the docs index links to `${langPrefix}/${externalPath}` instead of
  // the internal `/docs/${slug}` route. Used for cross-references to standalone
  // long-form pages (e.g. /camera-physics) that are not part of the docs corpus.
  externalPath?: string;
}

export interface DocsCategory {
  key: string;
  titleKey: string;
  icon: LucideIcon;
  order: number;
}

export const DOCS_CATEGORIES: DocsCategory[] = [
  { key: 'getting_started', titleKey: 'docs_category_getting_started', icon: Rocket, order: 1 },
  { key: 'core_features', titleKey: 'docs_category_core_features', icon: Settings, order: 2 },
  { key: 'account_settings', titleKey: 'docs_category_account_settings', icon: User, order: 3 },
];

export const DOCS_ARTICLES: DocsArticle[] = [
  // Getting Started
  { slug: 'getting-started', filename: 'GettingStarted', category: 'getting_started', titleKey: 'docs_getting_started_title', descriptionKey: 'docs_getting_started_desc', order: 1 },
  { slug: 'creating-a-project', filename: 'CreatingAProject', category: 'getting_started', titleKey: 'docs_creating_project_title', descriptionKey: 'docs_creating_project_desc', order: 2 },
  { slug: 'uploading-floor-plan', filename: 'UploadingFloorPlan', category: 'getting_started', titleKey: 'docs_uploading_floor_plan_title', descriptionKey: 'docs_uploading_floor_plan_desc', order: 3 },
  { slug: 'adding-cameras', filename: 'AddingCameras', category: 'getting_started', titleKey: 'docs_adding_cameras_title', descriptionKey: 'docs_adding_cameras_desc', order: 4 },
  { slug: 'field-of-view', filename: 'FieldOfView', category: 'getting_started', titleKey: 'docs_field_of_view_title', descriptionKey: 'docs_field_of_view_desc', order: 5 },
  { slug: 'indoor-plan-step-by-step', filename: 'IndoorPlanStepByStep', category: 'getting_started', titleKey: 'docs_indoor_plan_title', descriptionKey: 'docs_indoor_plan_desc', order: 6 },
  // Core Features
  { slug: 'cable-routing', filename: 'CableRouting', category: 'core_features', titleKey: 'docs_cable_routing_title', descriptionKey: 'docs_cable_routing_desc', order: 1 },
  { slug: 'pdf-export', filename: 'PdfExport', category: 'core_features', titleKey: 'docs_pdf_export_title', descriptionKey: 'docs_pdf_export_desc', order: 2 },
  { slug: 'storage-calculator', filename: 'StorageCalculatorDoc', category: 'core_features', titleKey: 'docs_storage_calculator_title', descriptionKey: 'docs_storage_calculator_desc', order: 3 },
  { slug: 'camera-physics', filename: '', category: 'core_features', titleKey: 'docs_camera_physics_title', descriptionKey: 'docs_camera_physics_desc', order: 4, externalPath: 'camera-physics' },
  // Account & Settings
  { slug: 'managing-projects', filename: 'ManagingProjects', category: 'account_settings', titleKey: 'docs_managing_projects_title', descriptionKey: 'docs_managing_projects_desc', order: 1 },
  { slug: 'subscription-plans', filename: 'SubscriptionPlans', category: 'account_settings', titleKey: 'docs_subscription_plans_title', descriptionKey: 'docs_subscription_plans_desc', order: 2 },
];

// External-link articles aren't part of the /docs/:slug route — exclude them
// from slug list and the slug→file map so SSG/router treat them as references only.
export const DOCS_SLUGS = DOCS_ARTICLES.filter(a => !a.externalPath).map(a => a.slug);

export function getOrderedArticles(): DocsArticle[] {
  return [...DOCS_ARTICLES].sort((a, b) => {
    const catA = DOCS_CATEGORIES.find(c => c.key === a.category)!;
    const catB = DOCS_CATEGORIES.find(c => c.key === b.category)!;
    if (catA.order !== catB.order) return catA.order - catB.order;
    return a.order - b.order;
  });
}

export function getPrevNextArticles(slug: string): { prev: DocsArticle | null; next: DocsArticle | null } {
  // External-link entries are skipped — prev/next only walks internal docs articles.
  const ordered = getOrderedArticles().filter(a => !a.externalPath);
  const idx = ordered.findIndex(a => a.slug === slug);
  return {
    prev: idx > 0 ? ordered[idx - 1] : null,
    next: idx < ordered.length - 1 ? ordered[idx + 1] : null,
  };
}

export function getArticlesByCategory(categoryKey: string): DocsArticle[] {
  return DOCS_ARTICLES.filter(a => a.category === categoryKey).sort((a, b) => a.order - b.order);
}

export const DOCS_SLUG_TO_FILE: Record<string, string> = Object.fromEntries(
  DOCS_ARTICLES.filter(a => !a.externalPath).map(a => [a.slug, a.filename])
);
